import React, { PureComponent, Fragment } from 'react';
import Wahanda from 'common/wahanda';
import App from 'common/backbone-app';
import classnames from 'classnames';
import { TableRow, TableData } from 'components/common/Table';
import { Badge } from 'components/common/Badge';
import { Currency, Time, TIME_TYPES } from 'components/common/DataFormat';
import { TooltipOverflow } from 'components/common/TooltipOverflow';
import {
  excludeRedeemedVoucherPayments,
  formatVoucherLabel,
  RedeemedVoucherLineItem,
} from '../../common/utils';
import style from './style.scss';
import {
  TRANSACTION_TYPE,
  TYPE,
  PAYMENT_TYPE,
  TransactionLineItem,
  TransactionPayment,
} from './types';

const lang = {
  headings: Wahanda.lang.reports.transactions.table.headings,
  payments: Wahanda.lang.reports.transactions.table.payments,
  badges: Wahanda.lang.reports.transactions.table.badges,
};
const uniqueKey = (props: Record<string, unknown>): string =>
  Object.values(props).reduce<string>(
    (accumulator, currentValue) => `${accumulator}-${currentValue}`,
    '',
  );
const MAX_VOUCHER_LENGHT = 45;
export interface ITransactionProps extends React.HTMLAttributes<Element> {
  lineItems?: TransactionLineItem[];
  description?: string;
  amount: number;
  advancedView?: boolean;
  customerName?: string;
  referenceTransactionId?: string;
  transactionId?: string;
  created: string;
  // Only set on quote/void rows — the backend sends this pre-combined and venue-local, so it's
  // rendered as-is. Everything else falls back to `created` + a client-side timezone conversion.
  venueLocalDateTime?: string;
  time: string;
  refundNote?: string;
  onClick?: (...args: any[]) => any;
  venueTransactionType?: string;
  isWalkIn?: boolean;
  isWithdrawal?: boolean;
  isDeposit?: boolean;
  isSale?: boolean;
  isRefund?: boolean;
  isQuote?: boolean;
  isTip?: boolean;
  quoteId?: number | string;
  type: typeof TYPE[keyof typeof TYPE];
  payments?: TransactionPayment[];
  redeemedVoucherLineItems?: RedeemedVoucherLineItem[];
}
class Transaction extends PureComponent<ITransactionProps, {}> {
  static defaultProps = {
    isWalkIn: false,
    isSale: false,
    venueTransactionType: null,
    customerName: null,
    isRefund: false,
    isDeposit: false,
    isWithdrawal: false,
    isQuote: false,
    isTip: false,
    description: null,
    lineItems: [],
    advancedView: false,
    refundNote: null,
    referenceTransactionId: null,
    transactionId: null,
    onClick: null,
    payments: [],
    redeemedVoucherLineItems: [],
  };

  onClick = () => {
    if (!this.props.onClick) {
      return;
    }
    this.props.onClick(this.props);
  };

  getPaymentTypeText = (payment: TransactionPayment) => {
    switch (payment.type) {
      case PAYMENT_TYPE.CASH:
        return lang.payments.cash;
      case PAYMENT_TYPE.PREPAID:
        return lang.payments.prepaid;
      case PAYMENT_TYPE.DEPOSIT:
        return lang.payments.deposit;
      case PAYMENT_TYPE.TAP_TO_PAY:
        return 'Tap To Pay';
      case PAYMENT_TYPE.TERMINAL:
        return lang.payments.terminal;
      case PAYMENT_TYPE.CARD:
        if (payment.description == null) {
          return lang.payments.card;
        }
        return `${lang.payments.card} (${payment.description})`;
      case PAYMENT_TYPE.VOUCHER:
        if (!payment.notes) {
          return payment.description;
        }
        return (
          <TooltipOverflow
            placement="top"
            text={`${payment.description} (${payment.notes})`}
            maxLength={MAX_VOUCHER_LENGHT}
          />
        );
      default:
        return null;
    }
  };

  getPaymentLines = () => {
    const { payments = [], redeemedVoucherLineItems = [], isRefund } = this.props;
    const paymentLines = excludeRedeemedVoucherPayments(payments, redeemedVoucherLineItems).map(
      (payment, index) => ({
        key: uniqueKey({ ...payment, index }),
        text: this.getPaymentTypeText(payment),
        amount: payment.amount,
      }),
    );
    const redeemedVoucherLines = redeemedVoucherLineItems.map((voucher, index) => ({
      key: `redeemed-voucher-${voucher.id ?? index}`,
      text: formatVoucherLabel(voucher, lang.payments.voucher),
      amount: isRefund ? -voucher.amount : voucher.amount,
    }));
    return [...paymentLines, ...redeemedVoucherLines];
  };

  renderReference = (text: string) => <div className={style.refunded}>{text}</div>;

  renderBadge = () => {
    const {
      isRefund,
      isDeposit,
      isSale,
      isWithdrawal,
      isQuote,
      isTip,
      venueTransactionType,
      type,
    } = this.props;
    if (isQuote) {
      if (type === TYPE.VOID) {
        return <Badge className={style.voidQuoteBadge} text={lang.badges.voidedQuote} />;
      }
      return <Badge className={style.quoteBadge} text={lang.badges.quote} />;
    }
    if (isRefund) {
      return <Badge type="alert" text={lang.badges.refund} />;
    }
    if (isDeposit) {
      return <Badge className={style.depositBadge} text={lang.badges.deposit} />;
    }
    if (isSale) {
      return <Badge className={style.saleBadge} text={lang.badges.sale} />;
    }
    if (isTip) {
      return <Badge className={style.tipBadge} text={lang.badges.tip} />;
    }
    if (isWithdrawal) {
      if (venueTransactionType === TRANSACTION_TYPE.CASH_REGISTER_CORRECTION_WITHDRAWAL) {
        return <Badge className={style.withdrawalBadge} text={lang.badges.drawerCorrection} />;
      }
      return <Badge className={style.withdrawalBadge} text={lang.badges.withdrawal} />;
    }
    return null;
  };

  renderAmount = (isVoidQuoteTableEntry: boolean) => {
    const { amount, lineItems = [], isWithdrawal } = this.props;

    if (isWithdrawal) {
      return <Currency amount={amount} />;
    }
    if (isVoidQuoteTableEntry) {
      return <div className={style.paymentDash}>-</div>;
    }
    return lineItems.map((item, index) => (
      <Currency key={uniqueKey({ ...item, index })} amount={item.amount} />
    ));
  };

  getReferenceId = (): string | undefined => {
    const { referenceTransactionId, isQuote } = this.props;

    if (referenceTransactionId && !isQuote) {
      return referenceTransactionId;
    }
    return undefined;
  };

  renderReferenceLabel = () => {
    const referenceId = this.getReferenceId();
    if (!referenceId) {
      return null;
    }
    return this.renderReference(`${lang.headings.refunds} ${referenceId}`);
  };

  render() {
    const {
      created,
      venueLocalDateTime,
      transactionId,
      customerName,
      description,
      refundNote,
      advancedView,
      amount,
      lineItems = [],
      onClick,
      isWalkIn,
      isQuote,
      isRefund,
      type,
    } = this.props;

    const isVoidQuoteTableEntry = !!isQuote && type === TYPE.VOID;
    const paymentLines = this.getPaymentLines();
    const amountClassName = isRefund ? style.refundAmount : undefined;
    const time = venueLocalDateTime ?? created;
    const timezone = venueLocalDateTime == null ? App.getTimezone() : undefined;

    return (
      <TableRow
        onClick={onClick ? this.onClick : undefined}
        hoverable
        dataTest={isQuote ? `quote-${transactionId}` : `transaction-${transactionId}`}
      >
        <TableData>
          <p className={style.time}>
            <Time type={TIME_TYPES.DATE_TIME} time={time} timezone={timezone} />
          </p>
          <div className={style.transactionIdSmall}>{transactionId}</div>
        </TableData>
        <TableData className={style.hideMobile}>{transactionId}</TableData>
        <TableData className={style.transactionType}>
          {this.renderBadge()}
          {this.renderReferenceLabel()}
        </TableData>
        <TableData
          data-hj-suppress
          className={classnames(style.customerName, isWalkIn ? style.noWrap : null)}
        >
          {customerName}
        </TableData>
        <TableData className={classnames(style.totalSum, amountClassName)}>
          <Currency amount={amount} />
        </TableData>
        {advancedView && (
          <Fragment>
            <TableData className={style.notes}>
              {lineItems.map((item, index) => (
                <div key={uniqueKey({ ...item, index })}>
                  {item.type === TYPE.TIP ? lang.badges.tip : item.name}
                </div>
              ))}
              {refundNote && this.renderReference(refundNote)}
              {description && <div className={style.description}>{description}</div>}
            </TableData>
            <TableData className={classnames(style.amount, amountClassName)}>
              {this.renderAmount(isVoidQuoteTableEntry)}
            </TableData>
            <TableData className={style.taxRate}>
              {isVoidQuoteTableEntry ? (
                <div className={style.paymentDash}>-</div>
              ) : (
                lineItems.map((item, index) => (
                  <div key={uniqueKey({ ...item, index })}>{`${item.taxRate}%`}</div>
                ))
              )}
            </TableData>
            <TableData className={style.taxAmount}>
              {isVoidQuoteTableEntry ? (
                <div className={style.paymentDash}>-</div>
              ) : (
                lineItems.map((item, index) => (
                  <div key={uniqueKey({ ...item, index })}>{item.taxAmount}</div>
                ))
              )}
            </TableData>
          </Fragment>
        )}
        <TableData className={style.paymentType}>
          {isQuote ? (
            <div className={style.paymentDash}>-</div>
          ) : (
            paymentLines.map((line) => (
              <div className={style.payment} key={line.key}>
                {line.text}
              </div>
            ))
          )}
        </TableData>
        <TableData className={classnames(style.paymentAmount, amountClassName)}>
          {isQuote ? (
            <div className={style.paymentDash}>-</div>
          ) : (
            paymentLines.map((line) => <Currency amount={line.amount} key={line.key} />)
          )}
        </TableData>
      </TableRow>
    );
  }
}

export default Transaction;
